import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, ShieldCheck, ShieldOff, UserPlus, X, ShieldAlert, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import FocusHeader from "@/components/FocusHeader";
import { TID } from "@/lib/testIds";
import { useAuth } from "@/context/AuthContext";
import { useRealtimeChannel } from "@/context/RealtimeContext";

const ROOT_EMAIL = "dan@virtusmaritime.com";

export default function AdminUsers() {
    const { user: currentUser } = useAuth();
    const isRoot = (currentUser?.email || "").toLowerCase() === ROOT_EMAIL;
    const nav = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInvite, setShowInvite] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load users.");
        } finally {
            setLoading(false);
        }
    };

    useRealtimeChannel("users", load);

    useEffect(() => {
        load();
    }, []);

    const toggleRole = async (u, role) => {
        const hasRole = (u.roles || []).includes(role);
        const nextRoles = hasRole
            ? (u.roles || []).filter((r) => r !== role)
            : [...(u.roles || []), role];
        try {
            const { data } = await api.patch(`/admin/users/${u.id}/roles`, {
                roles: nextRoles,
            });
            setUsers((prev) => prev.map((x) => (x.id === data.id ? data : x)));
            toast.success(`${u.email}: ${role} ${hasRole ? "revoked" : "granted"}.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed.");
        }
    };

    const remove = async (u) => {
        if (u.email === ROOT_EMAIL) {
            toast.warning("Cannot remove the root operator.");
            return;
        }
        if (!window.confirm(`Delete ${u.email}?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            setUsers((prev) => prev.filter((x) => x.id !== u.id));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed.");
        }
    };

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <FocusHeader
                code="ADMIN/USR"
                title="User Management"
                subtitle="Grant clearance · invite operators · manage roles."
            />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                    ROSTER // {users.length} OPERATOR{users.length === 1 ? "" : "S"}
                </div>
                <div className="flex items-center gap-2">
                    {isRoot && (
                        <button
                            onClick={() => nav("/admin/audit")}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim border border-virtus-line px-3 py-1.5 hover:border-virtus-orange hover:text-virtus-orange"
                            title="Root-only audit log"
                            data-testid={TID.adminAuditNavBtn}
                        >
                            <ShieldAlert size={11} /> AUDIT LOG
                        </button>
                    )}
                    <button
                        onClick={() => setShowInvite(true)}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black"
                        data-testid={TID.adminInviteBtn}
                    >
                        <UserPlus size={11} /> INVITE OPERATOR
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : (
                    <div>
                        <div className="grid grid-cols-[1fr_1fr_110px_110px_110px_130px_110px_60px] items-center gap-2 px-4 h-[26px] border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint sticky top-0 bg-virtus-bg z-10">
                            <div>EMAIL</div>
                            <div>NAME</div>
                            <div className="text-center">OPERATOR</div>
                            <div className="text-center">BD</div>
                            <div className="text-center">DOCS</div>
                            <div className="text-center" title="Grants download of production docs">
                                DOCS · DL
                            </div>
                            <div className="text-center">ADMIN</div>
                            <div />
                        </div>
                        <AnimatePresence>
                            {users.map((u) => {
                                const isRoot = u.email === ROOT_EMAIL;
                                const roles = u.roles || [];
                                return (
                                    <motion.div
                                        key={u.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="grid grid-cols-[1fr_1fr_110px_110px_110px_130px_110px_60px] items-center gap-2 px-4 h-11 border-b border-virtus-line hover:bg-[#141414]"
                                        data-testid={TID.adminUserRow(u.id)}
                                    >
                                        <div className="text-sm text-white truncate flex items-center gap-2">
                                            {u.email}
                                            {isRoot && (
                                                <span className="font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-orange bg-virtus-orange/10 border border-virtus-orange/40 px-1.5 py-0.5">
                                                    ROOT
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-mono text-xs text-virtus-dim truncate">
                                            {u.name || "—"}
                                        </div>
                                        <RoleToggle checked={true} disabled label="OPERATOR" />
                                        <RoleToggle
                                            checked={roles.includes("bd")}
                                            onChange={() => toggleRole(u, "bd")}
                                            label="BD"
                                            testId={TID.adminToggleBd(u.id)}
                                        />
                                        <RoleToggle
                                            checked={roles.includes("docs")}
                                            onChange={() => toggleRole(u, "docs")}
                                            label="DOCS"
                                            testId={TID.adminToggleDocs(u.id)}
                                        />
                                        <RoleToggle
                                            checked={roles.includes("docs_download")}
                                            onChange={() => toggleRole(u, "docs_download")}
                                            label="DOWNLOAD"
                                            testId={TID.adminToggleDocsDl(u.id)}
                                        />
                                        <RoleToggle
                                            checked={roles.includes("admin")}
                                            onChange={() => toggleRole(u, "admin")}
                                            disabled={isRoot}
                                            label="ADMIN"
                                            testId={TID.adminToggleAdmin(u.id)}
                                        />
                                        <button
                                            onClick={() => remove(u)}
                                            disabled={isRoot}
                                            className="text-virtus-faint hover:text-virtus-stuck disabled:opacity-30 disabled:cursor-not-allowed"
                                            data-testid={TID.adminDeleteUser(u.id)}
                                            aria-label="Delete user"
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {showInvite && (
                <InviteModal
                    isRoot={isRoot}
                    onClose={() => setShowInvite(false)}
                    onCreated={(u) => {
                        setUsers((prev) => {
                            const idx = prev.findIndex((x) => x.id === u.id);
                            if (idx >= 0) {
                                const copy = [...prev];
                                copy[idx] = u;
                                return copy;
                            }
                            return [...prev, u];
                        });
                    }}
                />
            )}
        </div>
    );
}

function RoleToggle({ checked, onChange, disabled, label, testId }) {
    return (
        <div className="flex justify-center">
            <button
                onClick={disabled ? undefined : onChange}
                disabled={disabled}
                data-testid={testId}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[10px] tracking-[0.22em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    checked
                        ? "border-virtus-done text-virtus-done bg-virtus-done/10"
                        : "border-virtus-line text-virtus-faint hover:border-virtus-orange hover:text-virtus-orange"
                }`}
            >
                {checked ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                {checked ? "GRANTED" : "OFF"}
            </button>
        </div>
    );
}

function InviteModal({ onClose, onCreated, isRoot }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [bd, setBd] = useState(false);
    const [docs, setDocs] = useState(false);
    const [docsDl, setDocsDl] = useState(false);
    const [admin, setAdmin] = useState(false);
    const [allowExternal, setAllowExternal] = useState(false);
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        const e = email.trim().toLowerCase();
        if (!e) return toast.warning("Email required.");
        const isInternal = e.endsWith("@virtusmaritime.com");
        if (!isInternal && !(isRoot && allowExternal)) {
            return toast.warning(
                isRoot
                    ? "Toggle 'Out-of-network' to invite a non-@virtusmaritime.com address."
                    : "Must be a @virtusmaritime.com address."
            );
        }
        const roles = ["operator"];
        if (bd) roles.push("bd");
        if (docs) roles.push("docs");
        if (docsDl) roles.push("docs_download");
        if (admin) roles.push("admin");
        setSaving(true);
        try {
            const { data } = await api.post("/admin/users/invite", {
                email: e,
                name: name.trim() || null,
                roles,
            });
            onCreated(data.user);
            toast.success(data.created ? "Operator invited." : "Roles updated.");
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Invite failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-md">
            <div className="w-[460px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange">
                <div className="flex items-center justify-between px-5 py-3 border-b border-virtus-line">
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                        INVITE OPERATOR
                    </div>
                    <button onClick={onClose} className="text-virtus-dim hover:text-virtus-orange" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                            EMAIL
                        </div>
                        <input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={allowExternal ? "guest@example.com" : "operator@virtusmaritime.com"}
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                            autoFocus
                            data-testid={TID.adminInviteEmail}
                        />
                        <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-faint mt-1">
                            {allowExternal
                                ? "Out-of-network invite · any domain allowed"
                                : "Must be @virtusmaritime.com"}
                        </div>
                        {isRoot && (
                            <label className="mt-2 flex items-center gap-2 cursor-pointer font-mono text-[10px] tracking-[0.2em] uppercase text-virtus-orange">
                                <input
                                    type="checkbox"
                                    checked={allowExternal}
                                    onChange={(e) => setAllowExternal(e.target.checked)}
                                    data-testid={TID.adminInviteExternal}
                                />
                                <ShieldAlert size={11} />
                                OUT-OF-NETWORK INVITE (ROOT ONLY)
                            </label>
                        )}
                    </div>
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-1.5">
                            NAME (OPTIONAL)
                        </div>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Full name"
                            className="w-full bg-transparent border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white outline-none"
                        />
                    </div>
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim mb-2">
                            INITIAL CLEARANCE
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 font-mono text-xs text-virtus-dim">
                                <ShieldCheck size={12} className="text-virtus-done" />
                                OPERATOR — granted by default (all standard focus areas)
                            </div>
                            <label className="flex items-center gap-2 cursor-pointer font-mono text-xs text-white">
                                <input
                                    type="checkbox"
                                    checked={bd}
                                    onChange={(e) => setBd(e.target.checked)}
                                    data-testid={TID.adminInviteRoleBd}
                                />
                                BD — grants access to BD Pipeline
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer font-mono text-xs text-white">
                                <input
                                    type="checkbox"
                                    checked={docs}
                                    onChange={(e) => setDocs(e.target.checked)}
                                    data-testid={TID.adminInviteRoleDocs}
                                />
                                DOCS — view + upload Production Documents
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer font-mono text-xs text-white">
                                <input
                                    type="checkbox"
                                    checked={docsDl}
                                    onChange={(e) => setDocsDl(e.target.checked)}
                                    data-testid={TID.adminInviteRoleDocsDl}
                                />
                                DOCS · DOWNLOAD — allow downloading Production Documents
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer font-mono text-xs text-white">
                                <input
                                    type="checkbox"
                                    checked={admin}
                                    onChange={(e) => setAdmin(e.target.checked)}
                                    data-testid={TID.adminInviteRoleAdmin}
                                />
                                ADMIN — grants ability to manage users
                            </label>
                        </div>
                    </div>
                </div>
                <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-virtus-line">
                    <button
                        onClick={onClose}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim hover:text-white px-3 py-2"
                    >
                        CANCEL
                    </button>
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="font-mono text-[10px] tracking-[0.22em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white glow-orange disabled:opacity-50"
                        data-testid={TID.adminInviteSubmit}
                    >
                        {saving ? "SAVING..." : "INVITE"}
                    </button>
                </div>
            </div>
        </div>
    );
}
